import { useState, useEffect } from "react";
import { fetchAPI } from "@/lib/api";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { FileUpload } from "@/components/admin/FileUpload";

interface TourPlan {
  id: number;
  slug: string;
  title: string;
  title_ja: string;
  subtitle: string;
  subtitle_ja: string;
  description: string;
  description_ja: string;
  duration_nights: number;
  duration_days: number;
  base_price: string;
  plan_type: string;
  featured_image_url: string;
  is_active: number;
  highlights: { badge_text: string, badge_text_ja: string, icon_name: string }[];
  sightseeing: { title: string, title_ja: string, image_url: string, sort_order: number }[];
  features: { feature_category: string, feature_category_ja: string, feature_text: string, feature_text_ja: string }[];
  terms: { tab_name: string, tab_name_ja: string, content: string, content_ja: string }[];
}

const initialFormState = {
  title: "",
  title_ja: "",
  slug: "",
  subtitle: "",
  subtitle_ja: "",
  description: "",
  description_ja: "",
  duration_nights: "3",
  duration_days: "4",
  base_price: "",
  plan_type: "European Plan",
  featured_image_url: "",
  is_active: true,
  highlights: [{ badge_text: "", badge_text_ja: "", icon_name: "CheckCircle2" }],
  sightseeing: [{ title: "", title_ja: "", image_url: "", sort_order: 0 }],
  features: [{ feature_category: "General", feature_category_ja: "一般", feature_text: "", feature_text_ja: "" }],
  terms: [{ tab_name: "", tab_name_ja: "", content: "", content_ja: "" }]
};

export default function TourPlansManager() {
  const [plans, setPlans] = useState<TourPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [currentPlan, setCurrentPlan] = useState<TourPlan | null>(null);
  const [formData, setFormData] = useState(initialFormState);
  const { toast } = useToast();

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = () => {
    setLoading(true);
    fetchAPI("?type=tour_plans")
      .then((data: any) => {
        setPlans(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching tour plans:", err);
        setLoading(false);
      });
  };

  const handleOpenDialog = (plan?: TourPlan) => {
    if (plan) {
      setCurrentPlan(plan);
      setFormData({
        title: plan.title,
        title_ja: plan.title_ja || "",
        slug: plan.slug,
        subtitle: plan.subtitle || "",
        subtitle_ja: plan.subtitle_ja || "",
        description: plan.description || "",
        description_ja: plan.description_ja || "",
        duration_nights: plan.duration_nights.toString(),
        duration_days: plan.duration_days.toString(),
        base_price: plan.base_price,
        plan_type: plan.plan_type || "",
        featured_image_url: plan.featured_image_url || "",
        is_active: plan.is_active === 1,
        highlights: plan.highlights?.length ? plan.highlights.map(h => ({
          badge_text: h.badge_text || "",
          badge_text_ja: h.badge_text_ja || "",
          icon_name: h.icon_name || "CheckCircle2"
        })) : [{ badge_text: "", badge_text_ja: "", icon_name: "CheckCircle2" }],
        sightseeing: plan.sightseeing?.length ? plan.sightseeing.map(s => ({
          title: s.title || "",
          title_ja: s.title_ja || "",
          image_url: s.image_url || "",
          sort_order: s.sort_order || 0
        })) : [{ title: "", title_ja: "", image_url: "", sort_order: 0 }],
        features: plan.features?.length ? plan.features.map(f => ({
          feature_category: f.feature_category || "General",
          feature_category_ja: f.feature_category_ja || "一般",
          feature_text: f.feature_text || "",
          feature_text_ja: f.feature_text_ja || ""
        })) : [{ feature_category: "General", feature_category_ja: "一般", feature_text: "", feature_text_ja: "" }],
        terms: plan.terms?.length ? plan.terms.map(t => ({
          tab_name: t.tab_name || "",
          tab_name_ja: t.tab_name_ja || "",
          content: t.content || "",
          content_ja: t.content_ja || ""
        })) : [{ tab_name: "", tab_name_ja: "", content: "", content_ja: "" }]
      });
    } else {
      setCurrentPlan(null);
      setFormData(initialFormState);
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const method = currentPlan ? "PUT" : "POST";
    const endpoint = currentPlan 
      ? `?type=tour_plans&id=${currentPlan.id}`
      : "?type=tour_plans";

    const submitData = {
        ...formData,
        duration_nights: parseInt(formData.duration_nights) || 0,
        duration_days: parseInt(formData.duration_days) || 0,
    };

    try {
      const result = await fetchAPI(endpoint, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(submitData),
      });
      if (result.status === "success") {
        toast({ title: "Success", description: "Tour Plan saved successfully" });
        setIsDialogOpen(false);
        fetchPlans();
      } else {
        throw new Error(result.message);
      }
    } catch (err: any) {
        toast({ variant: "destructive", title: "Error", description: err.message });
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Delete this tour plan?")) return;
    try {
      await fetchAPI(`?type=tour_plans&id=${id}`, { method: "DELETE" });
      toast({ title: "Deleted", description: "Tour plan removed" });
      fetchPlans();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-serif font-bold">Tour Plans</h1>
        <Button onClick={() => handleOpenDialog()} className="gap-2">
          <Plus className="w-4 h-4" /> Add Tour Plan
        </Button>
      </div>

      <div className="border rounded-lg shadow-sm bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead>Duration</TableHead>
              <TableHead>Price</TableHead>
              <TableHead>Active</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-4">Loading...</TableCell></TableRow>
            ) : plans.map((plan) => (
              <TableRow key={plan.id}>
                <TableCell className="font-medium max-w-[200px] truncate">
                  <div>{plan.title}</div>
                  {plan.title_ja && <div className="text-xs text-muted-foreground">{plan.title_ja}</div>}
                </TableCell>
                <TableCell className="max-w-[150px] truncate" title={plan.slug}>{plan.slug}</TableCell>
                <TableCell>{plan.duration_nights}N / {plan.duration_days}D</TableCell>
                <TableCell>₹{plan.base_price}</TableCell>
                <TableCell>{plan.is_active === 1 ? "Yes" : "No"}</TableCell>
                <TableCell className="text-right space-x-2">
                  <Button variant="outline" size="icon" onClick={() => handleOpenDialog(plan)}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button variant="destructive" size="icon" onClick={() => handleDelete(plan.id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{currentPlan ? "Edit Tour Plan" : "Add New Tour Plan"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Basic Info */}
            <div className="space-y-4">
              <h3 className="font-semibold text-lg border-b pb-2">Basic Information</h3>
              
              <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Title (English)</Label>
                    <Input value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} required />
                  </div>
                  <div className="space-y-2">
                    <Label>Title (Japanese)</Label>
                    <Input value={formData.title_ja} onChange={e => setFormData({...formData, title_ja: e.target.value})} />
                  </div>
              </div>

              <div className="space-y-2">
                <Label>Slug (URL Friendly Name)</Label>
                <Input value={formData.slug} onChange={e => setFormData({...formData, slug: e.target.value})} placeholder="e.g. mussoorie-premium" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Subtitle (English)</Label>
                    <Input value={formData.subtitle} onChange={e => setFormData({...formData, subtitle: e.target.value})} placeholder="The Queen of Hills" />
                  </div>
                  <div className="space-y-2">
                    <Label>Subtitle (Japanese)</Label>
                    <Input value={formData.subtitle_ja} onChange={e => setFormData({...formData, subtitle_ja: e.target.value})} />
                  </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Duration (Nights)</Label>
                  <Input type="number" value={formData.duration_nights} onChange={e => setFormData({...formData, duration_nights: e.target.value})} />
                </div>
                 <div className="space-y-2">
                  <Label>Duration (Days)</Label>
                  <Input type="number" value={formData.duration_days} onChange={e => setFormData({...formData, duration_days: e.target.value})} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                      <Label>Base Price</Label>
                      <Input type="number" step="0.01" value={formData.base_price} onChange={e => setFormData({...formData, base_price: e.target.value})} placeholder="27000.00" />
                  </div>
                   <div className="space-y-2">
                      <Label>Plan Type</Label>
                      <Input value={formData.plan_type} onChange={e => setFormData({...formData, plan_type: e.target.value})} placeholder="European Plan" />
                  </div>
              </div>

              <div className="space-y-2">
                  <Label>Featured Image</Label>
                  <FileUpload 
                      value={formData.featured_image_url} 
                      onChange={(url) => setFormData({...formData, featured_image_url: url})} 
                      accept="image/*"
                  />
              </div>

              <div className="space-y-2">
                <Label>Description (English)</Label>
                <Textarea rows={3} value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} />
              </div>

              <div className="space-y-2">
                <Label>Description (Japanese)</Label>
                <Textarea rows={3} value={formData.description_ja} onChange={e => setFormData({...formData, description_ja: e.target.value})} />
              </div>
            </div>

            {/* Highlights */}
            <div className="space-y-4 border-t pt-4">
              <div className="flex justify-between items-center">
                <h3 className="font-semibold text-lg">Highlights (Badges)</h3>
                <Button type="button" variant="outline" size="sm" onClick={() => setFormData({...formData, highlights: [...formData.highlights, {badge_text: "", badge_text_ja: "", icon_name: "Check"}]})}>
                  <Plus className="w-4 h-4 mr-2" /> Add Highlight
                </Button>
              </div>
              {formData.highlights.map((h, i) => (
                <div key={i} className="flex gap-4 items-center bg-muted/30 p-2 rounded-md">
                  <Input placeholder="Badge Text (English)" value={h.badge_text} onChange={e => {
                    const newH = [...formData.highlights]; newH[i].badge_text = e.target.value; setFormData({...formData, highlights: newH});
                  }} />
                  <Input placeholder="Badge Text (Japanese)" value={h.badge_text_ja} onChange={e => {
                    const newH = [...formData.highlights]; newH[i].badge_text_ja = e.target.value; setFormData({...formData, highlights: newH});
                  }} />
                  <Button type="button" variant="destructive" size="icon" onClick={() => {
                    const newH = [...formData.highlights]; newH.splice(i, 1); setFormData({...formData, highlights: newH});
                  }}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>

            {/* Sightseeing */}
            <div className="space-y-4 border-t pt-4">
              <div className="flex justify-between items-center">
                <h3 className="font-semibold text-lg">Sightseeing / Images</h3>
                <Button type="button" variant="outline" size="sm" onClick={() => setFormData({...formData, sightseeing: [...formData.sightseeing, {title: "", title_ja: "", image_url: "", sort_order: formData.sightseeing.length}]})}>
                  <Plus className="w-4 h-4 mr-2" /> Add Image
                </Button>
              </div>
              {formData.sightseeing.map((s, i) => (
                <div key={i} className="flex gap-4 items-start bg-muted/30 p-3 rounded-md">
                  <div className="flex-1 space-y-2">
                      <Input placeholder="Location Title (English)" value={s.title} onChange={e => {
                        const newS = [...formData.sightseeing]; newS[i].title = e.target.value; setFormData({...formData, sightseeing: newS});
                      }} />
                      <Input placeholder="Location Title (Japanese)" value={s.title_ja} onChange={e => {
                        const newS = [...formData.sightseeing]; newS[i].title_ja = e.target.value; setFormData({...formData, sightseeing: newS});
                      }} />
                      <FileUpload value={s.image_url} onChange={(url) => {
                         const newS = [...formData.sightseeing]; newS[i].image_url = url; setFormData({...formData, sightseeing: newS});
                      }} />
                  </div>
                  <Button type="button" variant="destructive" size="icon" onClick={() => {
                    const newS = [...formData.sightseeing]; newS.splice(i, 1); setFormData({...formData, sightseeing: newS});
                  }}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>

            {/* Features/Includes */}
            <div className="space-y-4 border-t pt-4">
              <div className="flex justify-between items-center">
                <h3 className="font-semibold text-lg">Features (Includes)</h3>
                <Button type="button" variant="outline" size="sm" onClick={() => setFormData({...formData, features: [...formData.features, {feature_category: "General", feature_category_ja: "一般", feature_text: "", feature_text_ja: ""}]})}>
                  <Plus className="w-4 h-4 mr-2" /> Add Feature
                </Button>
              </div>
              {formData.features.map((f, i) => (
                <div key={i} className="flex gap-4 items-center bg-muted/30 p-2 rounded-md">
                  <Input placeholder="Feature (English)" value={f.feature_text} onChange={e => {
                    const newF = [...formData.features]; newF[i].feature_text = e.target.value; setFormData({...formData, features: newF});
                  }} />
                  <Input placeholder="Feature (Japanese)" value={f.feature_text_ja} onChange={e => {
                    const newF = [...formData.features]; newF[i].feature_text_ja = e.target.value; setFormData({...formData, features: newF});
                  }} />
                  <Button type="button" variant="destructive" size="icon" onClick={() => {
                    const newF = [...formData.features]; newF.splice(i, 1); setFormData({...formData, features: newF});
                  }}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>

            {/* Terms and Conditions */}
            <div className="space-y-4 border-t pt-4">
              <div className="flex justify-between items-center">
                <h3 className="font-semibold text-lg">Terms & Conditions</h3>
                <Button type="button" variant="outline" size="sm" onClick={() => setFormData({...formData, terms: [...formData.terms, {tab_name: "", tab_name_ja: "", content: "", content_ja: ""}]})}>
                  <Plus className="w-4 h-4 mr-2" /> Add Term
                </Button>
              </div>
              {formData.terms.map((t, i) => (
                <div key={i} className="flex gap-4 items-start bg-muted/30 p-3 rounded-md">
                  <div className="flex-1 space-y-2">
                      <Input placeholder="Tab Name (English)" value={t.tab_name} onChange={e => {
                        const newT = [...formData.terms]; newT[i].tab_name = e.target.value; setFormData({...formData, terms: newT});
                      }} />
                      <Input placeholder="Tab Name (Japanese)" value={t.tab_name_ja} onChange={e => {
                        const newT = [...formData.terms]; newT[i].tab_name_ja = e.target.value; setFormData({...formData, terms: newT});
                      }} />
                      <Textarea placeholder="Policy Content (English)..." rows={2} value={t.content} onChange={e => {
                         const newT = [...formData.terms]; newT[i].content = e.target.value; setFormData({...formData, terms: newT});
                      }} />
                      <Textarea placeholder="Policy Content (Japanese)..." rows={2} value={t.content_ja} onChange={e => {
                         const newT = [...formData.terms]; newT[i].content_ja = e.target.value; setFormData({...formData, terms: newT});
                      }} />
                  </div>
                  <Button type="button" variant="destructive" size="icon" onClick={() => {
                    const newT = [...formData.terms]; newT.splice(i, 1); setFormData({...formData, terms: newT});
                  }}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>

            <div className="flex items-center gap-2 border-t pt-4">
              <Switch 
                checked={formData.is_active} 
                onCheckedChange={(checked) => setFormData({...formData, is_active: checked})} 
              />
              <Label>Active Status</Label>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
              <Button type="submit">Save Plan</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
